import { AnimatePresence } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import React, { Suspense, lazy, useMemo } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider as ReduxStoreProvider } from 'react-redux'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'

import { Toaster } from './components/ui/toaster'
import MainLayout from './Layouts/MainLayout'
import AdminLayout from './Layouts/AdminLayout' // NEW: Import AdminLayout
import { AuthProvider } from './lib/AuthContext'
import { ProtectedRoute } from './lib/ProtectedRoute'
import { store } from './redux/store'
import { OAuthWrapper } from './components/OAuthWrapper'
import { SlottingProvider } from './context/SlottingContext'

const SlottingSidebar = lazy(
  () => import('./components/custom/slotting/SlottingSidebar'),
)
const DefaultSlotting = lazy(
  () => import('./components/custom/slotting/DefaultSlotting'),
)
const SlottingConversation = lazy(
  () => import('./components/custom/slotting/SlottingConversation'),
)

// Lazy load route components for code-splitting
const WelcomePage = lazy(
  async () => import('./components/custom/welcome/WelcomePage'),
)
const LoginPage = lazy(async () => import('./app/login'))
const SignupPage = lazy(async () => import('./app/signup'))
const InputOTPForm = lazy(async () => import('./app/signup/otp-form'))
const ForgotPasswordForm = lazy(
  async () => import('./app/auth/forgot-password'),
)
const ResetPasswordOTPForm = lazy(
  async () => import('./app/auth/reset-password-otp'),
)
const ChatPageTransition = lazy(
  async () => import('./components/ChatPageTransition'),
)

// NEW: Lazy load components for existing pages
const ChatsSidebar = lazy(
  async () => import('./components/custom/chat/ChatsSidebar'),
)
const DefaultChat = lazy(
  async () => import('./components/custom/chat/DefaultChat'),
)
const ConversationChat = lazy(
  async () => import('./components/custom/chat/ConversationChat'),
)
const DashboardSidebar = lazy(
  async () => import('./components/custom/dashboard/DashboardSidebar'),
)
const DefaultDashboard = lazy(
  async () => import('./components/custom/dashboard/DefaultDashboard'),
)
const WidgetDashboard = lazy(
  async () => import('./components/custom/dashboard/WidgetDashboard'),
)

// EXISTING: Lazy load admin components
const QueryAnalyticsDashboard = lazy(
  async () => import('./pages/admin/QueryAnalyticsDashboard'),
)
const UserManagement = lazy(async () => import('./pages/admin/UserManagement'))
const SystemHealth = lazy(async () => import('./pages/admin/SystemHealth'))
const Performance = lazy(async () => import('./pages/admin/Performance'))
const AuditLogs = lazy(async () => import('./pages/admin/AuditLogs'))

// NEW: Lazy load business intelligence dashboards
const QueryIntelligenceDashboard = lazy(
  async () => import('./pages/admin/QueryIntelligenceDashboard'),
)
const UserAnalyticsDashboard = lazy(
  async () => import('./pages/admin/UserAnalyticsDashboard'),
)
const ContentKnowledgeDashboard = lazy(
  async () => import('./pages/admin/ContentKnowledgeDashboard'),
)
const PredictiveAnalyticsDashboard = lazy(
  async () => import('./pages/admin/PredictiveAnalyticsDashboard'),
)

// OTHER: Settings page
const Settings = lazy(async () => import('./pages/Settings'))

const App: React.FC = () => {
  // Memoize the QueryClient to avoid unnecessary re-instantiations
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            retry: false,
            refetchOnReconnect: false,
          },
        },
      }),
    [],
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ReduxStoreProvider store={store}>
        <AuthProvider>
          <BrowserRouter>
            <OAuthWrapper>
              <Toaster />
              <AnimatePresence mode="wait">
                <Suspense
                  fallback={
                    <div className="flex justify-center items-center h-screen">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </div>
                  }
                >
                  <Routes>
                    {/* 🔓 PUBLIC ROUTES - No Authentication Required */}
                    <Route path="/welcome" element={<WelcomePage />} />
                    <Route
                      path="/auth/login"
                      element={
                        <ChatPageTransition>
                          <LoginPage />
                        </ChatPageTransition>
                      }
                    />
                    <Route
                      path="/auth/signup"
                      element={
                        <ChatPageTransition>
                          <SignupPage />
                        </ChatPageTransition>
                      }
                    />

                    {/* 🔑 OTP and Password Reset Routes */}
                    <Route path="/auth/signup/otp" element={<InputOTPForm />} />
                    <Route
                      path="/auth/forgot-password"
                      element={<ForgotPasswordForm />}
                    />
                    <Route
                      path="/auth/reset-password-otp"
                      element={<ResetPasswordOTPForm />}
                    />

                    {/* 🔒 PROTECTED ROUTES - Authentication Required */}
                    <Route element={<ProtectedRoute />}>
                      {/* MAIN APP ROUTES - Use MainLayout */}
                      <Route element={<MainLayout />}>
                        <Route path="/chat" element={<ChatsSidebar />}>
                          <Route index element={<DefaultChat />} />
                          <Route
                            path="conversation/:conversationId/:conversationType"
                            element={<ConversationChat />}
                          />
                        </Route>
                        <Route path="/dashboard" element={<DashboardSidebar />}>
                          <Route index element={<DefaultDashboard />} />
                          <Route
                            path=":dashboardId"
                            element={<WidgetDashboard />}
                          />
                        </Route>
                        <Route
                          path="/slotting"
                          element={
                            <SlottingProvider>
                              <SlottingConversation />
                            </SlottingProvider>
                          }
                        />
                        <Route path="/settings" element={<Settings />} />
                      </Route>

                      {/* ADMIN ROUTES - Use AdminLayout */}
                      <Route path="/admin" element={<AdminLayout />}>
                        {/* Default redirect to query analytics */}
                        <Route
                          index
                          element={<Navigate to="/admin/analytics" replace />}
                        />

                        {/* Analytics & Intelligence Routes */}
                        <Route
                          path="analytics"
                          element={<QueryAnalyticsDashboard />}
                        />
                        <Route
                          path="query-intelligence"
                          element={<QueryIntelligenceDashboard />}
                        />
                        <Route
                          path="user-analytics"
                          element={<UserAnalyticsDashboard />}
                        />
                        <Route
                          path="content-knowledge"
                          element={<ContentKnowledgeDashboard />}
                        />
                        <Route
                          path="predictive-analytics"
                          element={<PredictiveAnalyticsDashboard />}
                        />

                        {/* System Management Routes */}
                        <Route path="users" element={<UserManagement />} />
                        <Route path="system" element={<SystemHealth />} />
                        <Route path="performance" element={<Performance />} />
                        <Route path="logs" element={<AuditLogs />} />
                      </Route>
                    </Route>

                    {/* 🔄 REDIRECTS */}
                    <Route path="/" element={<Navigate to="/chat" replace />} />

                    {/* 🚫 FALLBACK */}
                    <Route
                      path="*"
                      element={<Navigate to="/welcome" replace />}
                    />
                  </Routes>
                </Suspense>
              </AnimatePresence>
            </OAuthWrapper>
          </BrowserRouter>
        </AuthProvider>
      </ReduxStoreProvider>
    </QueryClientProvider>
  )
}

export default App
